import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { TrendingUp, Calendar, AlertTriangle, Sparkles, Clock, Target, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { db } from '../db';

const RunwayModal = ({ open, onClose, isPremium, onUpgrade, dailyBudget, totalSpent, daysElapsed }) => {
  const [tripData, setTripData] = useState(null);
  
  useEffect(() => {
    if (open) {
      loadTripData();
    }
  }, [open]);

  const loadTripData = async () => {
    try {
      const totalBudgetSetting = await db.settings.get('total_budget');
      const tripLengthSetting = await db.settings.get('trip_duration');
      const startDateSetting = await db.settings.get('trip_start_date');
      const currencySetting = await db.settings.get('currency');
      
      console.log('Loaded trip data:', {
        totalBudget: totalBudgetSetting?.value,
        tripLength: tripLengthSetting?.value,
        startDate: startDateSetting?.value,
        currency: currencySetting?.value
      });
      
      setTripData({
        totalBudget: parseFloat(totalBudgetSetting?.value || 0),
        tripLength: parseInt(tripLengthSetting?.value || 30),
        startDate: startDateSetting?.value || new Date().toISOString(),
        currency: currencySetting?.value || 'USD'
      });
    } catch (error) {
      console.error('Failed to load trip data:', error);
    }
  };

  // Calculate runway metrics
  const calculateRunway = () => {
    if (!tripData || !tripData.totalBudget) {
      return {
        dailyBurnRate: 0,
        remainingBudget: 0,
        runwayDays: 0,
        plannedDailyBudget: 0,
        status: 'unknown',
        endDate: null,
        daysOverUnder: 0
      };
    }

    const actualDaysElapsed = daysElapsed || 1;
    const dailyBurnRate = totalSpent / actualDaysElapsed;
    const remainingBudget = tripData.totalBudget - totalSpent;
    const runwayDays = dailyBurnRate > 0 ? Math.floor(remainingBudget / dailyBurnRate) : tripData.tripLength;
    const plannedDailyBudget = tripData.totalBudget / tripData.tripLength;
    
    // Calculate end date
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + runwayDays);
    
    // Determine status
    let status = 'on-track';
    const daysOverUnder = runwayDays - (tripData.tripLength - actualDaysElapsed);
    
    if (dailyBurnRate > plannedDailyBudget * 1.2) {
      status = 'overspending';
    } else if (dailyBurnRate < plannedDailyBudget * 0.8) {
      status = 'underspending';
    }

    return {
      dailyBurnRate,
      remainingBudget,
      runwayDays,
      plannedDailyBudget,
      status,
      endDate,
      daysOverUnder
    };
  };

  const runway = calculateRunway();
  const currency = tripData?.currency || 'USD';

  const getStatusColor = (status) => {
    switch (status) {
      case 'overspending': return 'text-destructive';
      case 'underspending': return 'text-primary';
      default: return 'text-foreground';
    }
  };

  const getStatusMessage = (status, daysOverUnder) => {
    switch (status) {
      case 'overspending':
        return `At this pace, you'll run out ${Math.abs(daysOverUnder)} days early`;
      case 'underspending':
        return `Great! You have ${daysOverUnder} extra days of runway`;
      default:
        return "You're right on track with your budget";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="runway-modal" className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-primary" />
            Understand Your Runway
          </DialogTitle>
          <p className="text-muted-foreground mt-2">
            How long can you keep traveling?
          </p>
        </DialogHeader>

        {/* What is Runway? */}
        <div className="bg-secondary/50 rounded-lg p-4 mt-4">
          <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            What is Runway?
          </h3>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Runway</strong> is how many days your money will last at your current spending rate. 
            It's your financial countdown — helping you make smarter decisions about where to go and what to do.
          </p>
        </div>

        {/* Current Runway Preview */}
        <div className="space-y-4 mt-4">
          <div className="text-center py-6 bg-gradient-to-br from-primary/10 to-secondary/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Your Current Runway</p>
            <p className="text-5xl font-heading font-bold text-primary">
              {runway.runwayDays}
            </p>
            <p className="text-lg text-foreground">days remaining</p>
            
            {runway.status !== 'unknown' && (
              <p className={`text-sm mt-2 ${getStatusColor(runway.status)}`}>
                {getStatusMessage(runway.status, runway.daysOverUnder)}
              </p>
            )}
          </div>

          {/* Basic Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-secondary/30 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground">Daily Burn Rate</p>
              <p className="text-lg font-bold text-foreground">
                {currency} {runway.dailyBurnRate.toFixed(0)}
              </p>
            </div>
            <div className="bg-secondary/30 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground">Remaining Budget</p>
              <p className="text-lg font-bold text-foreground">
                {currency} {runway.remainingBudget.toFixed(0)}
              </p>
            </div>
          </div>
        </div>

        {/* Premium Features Teaser */}
        {!isPremium && (
          <div className="mt-6 space-y-4">
            <div className="border border-primary/30 rounded-lg p-4 bg-primary/5">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Unlock Full Runway Intelligence
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">30/60/90 Day Projections</p>
                    <p className="text-xs text-muted-foreground">See exactly when your budget runs out</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">What-If Scenarios</p>
                    <p className="text-xs text-muted-foreground">"If I spend 20% less, I gain 12 more days"</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Smart Alerts</p>
                    <p className="text-xs text-muted-foreground">Get warned before you overspend</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">AI Budget Analysis</p>
                    <p className="text-xs text-muted-foreground">Personalized tips to stretch your runway</p>
                  </div>
                </div>
              </div>
            </div>

            <Button
              data-testid="upgrade-runway-button"
              onClick={onUpgrade}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold shadow-primary hover:shadow-primary-hover transition-all duration-300"
            >
              Unlock Premium
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Premium Full View */}
        {isPremium && (
          <div className="mt-6 space-y-4">
            {/* Extended Projections */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-secondary/30 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">30 Days</p>
                <p className="text-xl font-bold text-primary">
                  {Math.floor((runway.remainingBudget - (runway.dailyBurnRate * 30)))}
                </p>
                <p className="text-xs text-muted-foreground">{currency} left</p>
              </div>
              <div className="bg-secondary/30 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">60 Days</p>
                <p className="text-xl font-bold text-primary">
                  {Math.floor((runway.remainingBudget - (runway.dailyBurnRate * 60)))}
                </p>
                <p className="text-xs text-muted-foreground">{currency} left</p>
              </div>
              <div className="bg-secondary/30 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">90 Days</p>
                <p className="text-xl font-bold text-primary">
                  {Math.floor((runway.remainingBudget - (runway.dailyBurnRate * 90)))}
                </p>
                <p className="text-xs text-muted-foreground">{currency} left</p>
              </div>
            </div>

            {/* Predicted End Date */}
            {runway.endDate && (
              <div className="bg-secondary/30 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">Predicted Budget End Date</p>
                <p className="text-xl font-bold text-foreground mt-1">
                  {runway.endDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            )}

            {/* What-If Scenarios */}
            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-3">What-If Scenarios</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Spend 10% less daily</span>
                  <span className="text-primary font-medium">
                    +{Math.floor(runway.runwayDays * 0.11)} days
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Spend 20% less daily</span>
                  <span className="text-primary font-medium">
                    +{Math.floor(runway.runwayDays * 0.25)} days
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Spend 10% more daily</span>
                  <span className="text-destructive font-medium">
                    -{Math.floor(runway.runwayDays * 0.09)} days
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <Button
          data-testid="close-runway-modal-button"
          onClick={onClose}
          variant="ghost"
          className="w-full mt-2 text-muted-foreground"
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default RunwayModal;
